import Vue from "vue";
import PaymentHelper from "@/utils/payment-helper"
import { routesToTrack, addPrefixToRoute } from "@/utils/checkout-router-helper"
const state = {
  has_taxes: false,
  has_einvoicing: false,
  has_metered_items: false,
  has_multidecimal: false,
  has_hvc: false,
  selected_payment_method: {},
  total_order_value: 0,
  currency_code: undefined,
  invoice_count: 0,
  coupons_count: {},
  client_version: process.env.CB_BUILD_TAG,
  hp_user_navigation_stats: [],
  has_logs_pushed: false,
};

const LOG_HAS_TAXES = "LOG_HAS_TAXES";
const LOG_HAS_EINVOICING = "LOG_HAS_EINVOICING";
const LOG_PAYMENT_METHOD = "LOG_PAYMENT_METHOD";
const LOG_TOTAL_ORDER_VALUE = "LOG_TOTAL_ORDER_VALUE";
const LOG_CURRENCY_CODE = "LOG_CURRENCY_CODE";
const LOG_HAS_METERED_ITEMS = "LOG_HAS_METERED_ITEMS";
const LOG_HAS_MULTIDECIMAL = "LOG_HAS_MULTIDECIMAL";
const LOG_COUPONS_COUNT = "LOG_COUPONS_COUNT";
const LOG_HAS_HVC = "LOG_HAS_HVC";
const LOG_INVOICE_COUNT = "LOG_INVOICE_COUNT";
const LOG_USER_NAVIGATION_STAT = "LOG_USER_NAVIGATION_STAT";
const LOG_HAS_PUSHED = "LOG_HAS_PUSHED";

export const PAYMENT_MODE = {
  OFFLINE: 'offline',
  ONLINE: 'online',
  PAYLESS: 'payless',
};

function getPaymentMode(rootGetters, pm = {}) {
  if (PaymentHelper.isOfflinePaymentMethod(pm.type)) {
    return PAYMENT_MODE.OFFLINE;
  }

  if (!!rootGetters.is_payless_checkout) {
    return PAYMENT_MODE.PAYLESS;
  }

  return PAYMENT_MODE.ONLINE
}

const actions = {
  log_has_taxes({ commit }, hasTaxes) {
    commit(LOG_HAS_TAXES, hasTaxes);
  },
  log_has_metered_items({ commit }, hasMeteredItems) {
    commit(LOG_HAS_METERED_ITEMS, hasMeteredItems)
  },
  log_has_einvoicing({ commit }, hasEinvoicing) {
    commit(LOG_HAS_EINVOICING, hasEinvoicing);
  },
  log_selected_payment_method({ commit, rootGetters }, pm = {}) {
    const {
      type: payment_method = "",
      gateway_name: payment_gateway = "",
      gateway = "",
    } = pm;
    const payload = {
      payment_mode: getPaymentMode(rootGetters, pm),
      payment_method,
      payment_gateway: payment_gateway || gateway
    };
    commit(LOG_PAYMENT_METHOD, payload);
  },
  log_total_order_value({ commit }, value) {
    commit(LOG_TOTAL_ORDER_VALUE, value);
  },
  log_currency_code({ commit }, currencyCode) {
    commit(LOG_CURRENCY_CODE, currencyCode);
  },
  log_has_multidecimal({ commit }, value) {
    commit(LOG_HAS_MULTIDECIMAL, value);
  },
  log_coupons_count({ commit }, couponCountObj) {
    commit(LOG_COUPONS_COUNT, couponCountObj);
  },
  log_has_hvc({ commit }, hasHvc) {
    commit(LOG_HAS_HVC, hasHvc);
  },
  log_transaction({ commit, state }, transaction) {
    const pmDetails = {
      payment_mode: 'online',
      payment_method: transaction.payment_method,
      payment_gateway: transaction.gateway
    };
    commit(LOG_PAYMENT_METHOD, pmDetails);
    commit(LOG_CURRENCY_CODE, transaction.currency_code);
    commit(LOG_TOTAL_ORDER_VALUE, transaction.amount);
    commit(LOG_INVOICE_COUNT, (transaction.linked_invoices || []).length)
  },
  transmit_log_data({ getters }) {
    logger.kvl(getters.sanitized_logs);
  },
  log_user_navigation_stat({ commit }, stat) {
    commit(LOG_USER_NAVIGATION_STAT, stat);
  },
  log_has_pushed({ commit }, data) {
    commit(LOG_HAS_PUSHED, data);
  },
};

const mutations = {
  [LOG_HAS_TAXES](state, hasTaxes) {
    Vue.set(state, "has_taxes", hasTaxes);
  },
  [LOG_HAS_EINVOICING](state, hasEinvoicing) {
    Vue.set(state, "has_einvoicing", hasEinvoicing);
  },
  [LOG_PAYMENT_METHOD](state, paymentDetails) {
    Vue.set(state, "selected_payment_method", paymentDetails);
  },
  [LOG_TOTAL_ORDER_VALUE](state, value) {
    Vue.set(state, "total_order_value", value);
  },
  [LOG_CURRENCY_CODE](state, currencyCode) {
    Vue.set(state, "currency_code", currencyCode);
  },
  [LOG_HAS_METERED_ITEMS](state, hasMeteredItems) {
    Vue.set(state, "has_metered_items", hasMeteredItems);
  },
  [LOG_HAS_MULTIDECIMAL](state, hasMultiDecimal) {
    Vue.set(state, "has_multidecimal", hasMultiDecimal);
  },
  [LOG_COUPONS_COUNT](state, couponCountObj) {
    Vue.set(state, "coupons_count", couponCountObj);
  },
  [LOG_HAS_HVC](state, hasHvc) {
    Vue.set(state, "has_hvc", hasHvc);
  },
  [LOG_INVOICE_COUNT](state, count) {
    Vue.set(state, "invoice_count", count);
  },
  [LOG_USER_NAVIGATION_STAT](state, payload) {
    if (Object.keys(payload).length === 0) return;
    let stats = state.hp_user_navigation_stats.filter(stat => stat.key == payload.key)
    if (stats.length > 0) {
      const lastStat = stats[stats.length - 1];
      if (lastStat.type != payload.type) {
        state.hp_user_navigation_stats.push(payload);
      }
    } else {
      state.hp_user_navigation_stats.push(payload);
    }
  },
  [LOG_HAS_PUSHED](state, data) {
    Vue.set(state, "has_logs_pushed", data);
  },
};

const getters = {
  hp_logs(state, rootState, getters, rootGetters) {
    let data = Object.assign({
      hp_token: rootGetters.hpToken,
      hp_trace_id: rootGetters.sessionToken,
      hp_product_catalog_ver: rootGetters.product_catalog_ver,
      hp_type: rootGetters.hp_type,
      ...rootGetters.payment_logs,
      ...rootGetters.hp_nav_time_stats,
      ...rootGetters.hp_total_time_spent,
      ...rootGetters.get_last_entry_route
    }, state);

    delete data.hp_user_navigation_stats;
    delete data.has_logs_pushed;

    return data;
  },

  payment_logs(state) {
    const { payment_method, payment_mode, payment_gateway } = state.selected_payment_method;
    return {
      payment_gateway: payment_gateway,
      payment_method: payment_method,
      payment_mode: payment_mode,
    }
  },
  coupon_logs(state) {
    const log = {};
    Object.keys(state.coupons_count).forEach(key => {
      log[`${key}_count`] = state.coupons_count[key];
    })
    return log;
  },
  send_logs: (state, getters) => (payload) => {
    try {
      if (payload) {
        payload.hp_logs = JSON.stringify(getters.hp_logs)
      }
    } catch (error) {
      logger.e(error);
    }
  },
  sanitized_logs: (state, getters) => {
    const logs = {
      ...getters.hp_logs,
      ...getters.payment_logs,
      ...getters.coupon_logs
    };

    // Remove complex types
    delete logs.selected_payment_method;
    delete logs.coupons_count
    delete logs.hp_user_navigation_stats;
    delete logs.has_logs_pushed;

    // Prefix hp_ to all store data
    Object.keys(logs).forEach(key => {
      if (!key.startsWith('hp_')) {
        logs[`hp_${key}`] = logs[key];
        delete logs[key];
      }
    })

    return logs;
  },
  get_last_entry_route(state) {
    let lastEntryRoute = {}
    let routeEnterStats = state.hp_user_navigation_stats
        .filter((stat => (stat.type == 'entry')));
    if (routeEnterStats
      && routeEnterStats.length > 0) {
        lastEntryRoute = { hp_exit_page: routeEnterStats[routeEnterStats.length - 1].key}
    }
    return lastEntryRoute;
  },
  hp_nav_time_stats(state) {
    let navigationStats = {};
    try {
      Object.keys(routesToTrack).forEach(function (key, index) {
        let route = addPrefixToRoute(key);
        let routeEnterStats = state.hp_user_navigation_stats
          .filter((stat => (stat.type == 'entry') && (stat.key == route)));
        let routeExitStats = state.hp_user_navigation_stats
          .filter((stat => (stat.type == 'exit') && (stat.key == route)));

        if (routeEnterStats
          && routeExitStats
          && routeExitStats.length > 0
          && routeEnterStats.length > 0) {
          for (let i = 0; i < routeEnterStats.length; i++) {
            if (routeEnterStats[i] && routeExitStats[i]) {
              let existingTime = navigationStats.hasOwnProperty(route) ? navigationStats[route] : 0;
              let routeExitTime = routeExitStats[i].time;
              let routeEnterTime = routeEnterStats[i].time;
              let totalTime = existingTime + Math.floor(((routeExitTime - routeEnterTime)) / 1000 % 60);
              navigationStats[route] = totalTime;
            }
          }
        }
      });
    } catch (error) {
      logger.e(error);
    }
    return navigationStats;
  },
  hp_total_time_spent(state, getters) {
    try {
      let stats = getters.hp_nav_time_stats;
      let time = 0;
      Object.keys(stats).forEach(function (key, index) {
        time = time + stats[key];
      });
      return { hp_total_time: time };
    } catch (error) {
      logger.e(error)
    }
  },
  has_logs_pushed(state) {
    return state.has_logs_pushed;
  }
};
export default {
  state,
  actions,
  mutations,
  getters
};
